/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { Capability } from '../../constants/capabilities';
import { cloneDeep } from 'lodash';

export function mapPreviousCapabilitiesByStateFromUrl(
	capabilities: ReadonlyArray<Capability>,
	editorPlugins: Array<string>
): ReadonlyArray<Capability> {
	return capabilities.map( capability => {
		if ( !capability.hasToggle ) {
			return capability;
		}

		// Check if any feature from the "ON" state is selected.
		const hasToggleOnPlugins = ( capability.toggleValue ? capability.features : capability.toggleFeatures! )
			.filter( feature => feature.name !== feature.toggleFeatureName )
			.some( feature => {
				return feature.plugins.find( pluginName => editorPlugins.includes( pluginName ) );
			} );
		// Check if any feature from the "OFF" state is selected.
		const hasToggleOffPlugins = ( !capability.toggleValue ? capability.features : capability.toggleFeatures! )
			.filter( feature => feature.name !== feature.toggleFeatureName )
			.some( feature => {
				return feature.plugins.find( pluginName => editorPlugins.includes( pluginName ) );
			} );

		// When switch should be marked as "ON".
		if ( hasToggleOnPlugins ) {
			// And it's already marked, do nothing.
			if ( capability.toggleValue ) {
				return capability;
			}

			// Otherwise, mark as "ON" and swap its features.
			const clone = cloneDeep( capability );

			clone.toggleValue = true;
			[ clone.features, clone.toggleFeatures ] = [ clone.toggleFeatures!, clone.features ];

			return clone;
		}
		// When switch should be marked as "OFF".
		else if ( hasToggleOffPlugins ) {
			// And it's already marked, do nothing.
			if ( !capability.toggleValue ) {
				return capability;
			}

			// Otherwise, mark as "OFF" and swap its features.
			const clone = cloneDeep( capability );

			clone.toggleValue = false;
			[ clone.features, clone.toggleFeatures ] = [ clone.toggleFeatures!, clone.features ];

			return clone;
		}

		// If cannot determine what to do, do nothing.
		return capability;
	} );
}
